import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { StepIntro, TextField, YesNo } from '../fields';
import { DomainInput } from '../DomainInput';

export function StepReviews({ data, set }) {
  const rows = Array.isArray(data) ? data : [];
  const upd = (i, k, v) => set(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="space-y-4">
      <StepIntro title="Customer reviews" text="Optional — real reviews build trust. Add as many as you like." />
      {rows.map((r, i) => (
        <div key={i} className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid={`ww-review-${i}`}>
          <div className="grid sm:grid-cols-2 gap-3">
            <Input value={r.customer_name || ''} onChange={(e) => upd(i, 'customer_name', e.target.value)} placeholder="Customer name" data-testid={`ww-review-${i}-name`} className="h-11" />
            <Input value={r.location || ''} onChange={(e) => upd(i, 'location', e.target.value)} placeholder="Location (e.g. Stockport)" data-testid={`ww-review-${i}-location`} className="h-11" />
          </div>
          <Textarea value={r.review || ''} onChange={(e) => upd(i, 'review', e.target.value)} placeholder="What did they say?" rows={3} data-testid={`ww-review-${i}-text`} />
          <div className="flex gap-2">
            <Input value={r.service || ''} onChange={(e) => upd(i, 'service', e.target.value)} placeholder="Service completed (e.g. Resin bound driveway)" data-testid={`ww-review-${i}-service`} className="h-11 flex-1" />
            <Button type="button" variant="outline" size="icon" className="h-11 w-11 text-rose-600" onClick={() => set(rows.filter((_, j) => j !== i))} data-testid={`ww-review-${i}-remove`}><Trash2 className="w-4 h-4" /></Button>
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" onClick={() => set([...rows, {}])} data-testid="ww-review-add" className="w-full h-11"><Plus className="w-4 h-4 mr-1" /> Add a review</Button>
    </div>
  );
}

export function StepDomain({ data, set }) {
  const f = (k) => (v) => set({ ...data, [k]: v });
  const onResult = (domain, status) => set((cur) => ({ ...(cur || {}), checks: { ...((cur || {}).checks || {}), [domain]: status } }));
  return (
    <div className="space-y-4">
      <StepIntro title="Your domain" text="Your web address, e.g. smithresin.co.uk. Ox Resin buys and sets this up for you." />
      <YesNo id="has_domain" label="Do you already have a domain?" value={data.has_domain} onChange={f('has_domain')} />
      {data.has_domain === 'yes' && (
        <>
          <TextField id="domain_name" label="Domain name" value={data.domain_name} onChange={f('domain_name')} placeholder="yourbusiness.co.uk" inputMode="url" />
          <TextField id="provider" label="Current provider" value={data.provider} onChange={f('provider')} optional placeholder="e.g. GoDaddy, 123-reg, Wix" hint="We'll be in touch about pointing it at your new site." />
        </>
      )}
      {data.has_domain === 'no' && (
        <>
          <DomainInput id="preferred" label="Preferred domain" value={data.preferred} onChange={f('preferred')} placeholder="smithresin.co.uk" onResult={onResult} />
          <DomainInput id="second" label="Second choice" value={data.second} onChange={f('second')} optional placeholder="smith-resin.co.uk" onResult={onResult} />
          <DomainInput id="third" label="Third choice" value={data.third} onChange={f('third')} optional placeholder="smithresindriveways.co.uk" onResult={onResult} />
          <p className="text-xs text-slate-500 rounded-lg bg-amber-50 border border-amber-200 p-3" data-testid="ww-domain-note">We check availability as you type, but it's only a guide — domains are subject to availability and we'll confirm the first one that's free when we register it.</p>
        </>
      )}
    </div>
  );
}

export function StepEmails({ data, set }) {
  const rows = Array.isArray(data) ? data : [];
  const upd = (i, k, v) => set(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const quick = ['info', 'sales', 'enquiries', 'accounts'];
  return (
    <div className="space-y-4">
      <StepIntro title="Email addresses" text="Professional email on your own domain. Tell us the addresses you'd like and where to forward them." />
      <div className="flex flex-wrap gap-2">
        {quick.map((q) => <button key={q} type="button" disabled={rows.some((r) => r.prefix === q)} onClick={() => set([...rows, { prefix: q, forward_to: '' }])} data-testid={`ww-email-quick-${q}`} className="text-xs font-semibold px-3 py-1.5 rounded-full border border-slate-300 bg-white disabled:opacity-40 hover:border-slate-500">+ {q}@</button>)}
      </div>
      {rows.map((r, i) => (
        <div key={i} className="flex flex-col sm:flex-row gap-2 rounded-xl border border-slate-200 bg-white p-3" data-testid={`ww-email-${i}`}>
          <div className="flex items-center gap-1 flex-1"><Input value={r.prefix || ''} onChange={(e) => upd(i, 'prefix', e.target.value.replace(/[^a-z0-9.-]/gi, '').toLowerCase())} placeholder="firstname" data-testid={`ww-email-${i}-prefix`} className="h-11" /><span className="text-sm text-slate-500 shrink-0">@ your domain</span></div>
          <Input value={r.forward_to || ''} onChange={(e) => upd(i, 'forward_to', e.target.value)} placeholder="Forward to (existing email)" type="email" data-testid={`ww-email-${i}-forward`} className="h-11 flex-1" />
          <Button type="button" variant="outline" size="icon" className="h-11 w-11 text-rose-600 shrink-0" onClick={() => set(rows.filter((_, j) => j !== i))} data-testid={`ww-email-${i}-remove`}><Trash2 className="w-4 h-4" /></Button>
        </div>
      ))}
      <Button type="button" variant="outline" onClick={() => set([...rows, { prefix: '', forward_to: '' }])} data-testid="ww-email-add" className="w-full h-11"><Plus className="w-4 h-4 mr-1" /> Add another email</Button>
    </div>
  );
}
